// ============================================================
// BALANCE — 統一數值調整面板（單一信任來源）
// ============================================================
//
// 這份檔案集中所有「可以為了平衡性而被設計師調整」的數字。
// 改完 `npm run dev` 會自動 hot-reload，瀏覽器重新整理即可實測。
//
// ─────────────────────────────────────────────────────────────
// 🎯 快速索引：想調整 X，改哪一段？
// ─────────────────────────────────────────────────────────────
//   想做什麼                                          | 去哪
//   ─────────────────────────────────────────────────|──────
//   讓某兵種更耐打 / 提高攻擊                          | §1 UCFG.<type>.hp / .atk
//   調整法術傷害 / 治療強度 / 範圍半徑                | §2 SPELLS.<element>
//   調整基地血量（場次節奏）                          | §3 BASES.hp
//   調整連鎖加成 / 法術升等速度                       | §4 MATCH3_CHAIN_MULT / SPELL_LEVEL_THRESHOLDS
//   調整 30 秒倒數 / 10 swap 上限                     | §5
//   調整治療佔比 (HEAL_RATIO)                         | §7
//   調整 Shield Wall 各等級擋多少                     | §7 SHIELD_WALL_*_BY_POWER
//   調整燒草 DOT / Lightning 積水加成                  | §8
//   調整地形 cluster 大小 / 數量 / 範圍               | §9
//   調整 manual 模式兵種上限（含未來 3v3 / 10v10）      | §10 MANUAL_RULES
//   調整 AI 放置算法（重試次數 / 路徑步數）              | §10 AI_MAX_PLACEMENT_ATTEMPTS / AI_MIN_PATH_STEPS
//
// ─────────────────────────────────────────────────────────────
// ⚠️ 安全須知
// ─────────────────────────────────────────────────────────────
//   1. 本檔與 `puzzle-tactician-server/src/balance.ts` **必須 byte-identical**。
//      改完用 `cp` 同步（diff 應為空）— 與 src/server/* 同等級守則。
//      也可以執行 .\build-all.ps1 會自動同步。
//   2. 別動 ELEM / ECOL / BATTLE_COLS / 型別定義 — 那些是結構常數，
//      留在 constants.ts；改動會破壞 ELEM 與 SPELLS / ECOL 的 key 對應。
//   3. 想加「新的可調參數」：先在這裡加 const，再讓邏輯端 import。
//   4. 想加「新機制」（如新法術 type、新地形互動）：規格書 v2 先談設計，
//      再動 spell.ts / combat.ts / tick.ts。本檔只放數字。
//
// ============================================================

import type {
  UnitConfig,
  BaseConfig,
  SpellDef,
  M3Config,
  LeaderDef,
  LeaderId,
} from './constants.ts';

// ============================================================
// §1 兵種屬性
// ============================================================
//
// 每個 key 是兵種名稱（與 ELEM 無關；這裡的 key 對應 Deployment palette
// 與 mkUnit factory 的 type 字串）。
//
// 欄位含意：
//   - hp        : 滿員兵力。也是治療封頂值（heal 不能超過 maxSoldiers）
//   - atk       : 每次攻擊造成的傷害 = `Math.ceil(totalSoldiers × atk)`
//                 Infantry / Archer 是 0.5 → 兵力一半時剛好打 0 傷害
//                 想避免「半血變廢」可以拉高；想強化前線消耗戰可以再壓低
//   - range     : Chebyshev 距離（1 = 鄰接 8 格、2 = 5×5 範圍）
//   - speed     : 每 tick 最大移動格數
//                 ⚠️ speed=2 才有 movement.ts 的「兩階段移動」與
//                    puddle slow（積水減 1）— 改成 3+ 會落入未測試的分支
//   - maxPath   : 部屬時最多能畫幾格路徑（UX 限制，不影響戰鬥）
//   - shape     : Icon 樣式（純視覺，由 Renderer._drawUnitShape 對應到圖形）
//
// 平衡性方向參考：
//   - HP ↑ → 對打更耐久 / 法術相對較弱（法術 dmg 是固定值）
//   - ATK ↑ → 戰場推進變快、防守端壓力大
//   - speed ↑ → 翻盤機率高（騎兵已經很 OP）；speed=1 → 穩定推進
//
// ⚠️ 改完兵種列表（新增 / 刪除）：UCFG / SPELLS / mkUnit / MANUAL_RULES 的
//    composition 都要對齊；新增建議走 CODE-MAP 的「新 unit type」流程。

export const UCFG: Record<string, UnitConfig> = {
  Infantry: { hp: 60, atk: 0.5, range: 1, speed: 1, shape: 'sword',     maxPath: 10 },
  Cavalry:  { hp: 40, atk: 1,   range: 1, speed: 2, shape: 'horseshoe', maxPath: 14 },
  Archer:   { hp: 35, atk: 0.5, range: 2, speed: 1, shape: 'bow',       maxPath: 10 },
  Mage:     { hp: 25, atk: 1,   range: 2, speed: 1, shape: 'wand',      maxPath: 10 },
};

// ============================================================
// §2 法術定義 (Session 10 — effects-list 模型)
// ============================================================
//
// 每個法術現在是 SpellDef，包含：
//   - name / radius   : 顯示名 + Manhattan 半徑
//   - phase           : 'friendly' (Step A) / 'hostile' (Step D)
//   - scope           : 'own' (打/補我方) / 'enemy' (打/影響敵方)
//   - effects         : 一連串 EffectKind，每個獨立套用到範圍內的目標
//
// EffectKind 支援的種類（定義在 constants.ts EffectKind union）：
//   - damage        傷害（可選 puddleBonus / ignite）
//   - heal          回血（ratio × maxSoldiers）
//   - buffAtk       加 / 減攻擊力（負值 = debuff）
//   - shieldPhys    物理減免 (Shield Wall 物理腿)
//   - shieldMag     法術減免 (Shield Wall 法術腿)
//   - whirlwindPull 把目標往中心位移一格
//
// 數值用 EffectScaling 表達：
//   - linear { perLevel: N }     → value = N × powerLevel  （= 舊版 dmg × power 風格）
//   - table  { byPower: [...] }  → value = byPower[powerLevel] （每等級獨立曲線）
//   - flat   { value: N }        → 同值不縮放
//
// 平衡性方向：
//   - 加新副效果 → 在某法術的 effects[] 多 push 一個 EffectKind
//   - 想改某等級單獨的數值 → 把 linear 換成 table 後逐格調
//   - 想做「打敵 + 補我」式混合 → 一個 effect 帶 scope: 'own' 另一個 scope: 'enemy'
//
// ⚠️ SPELLS 物件實際定義在「本檔最尾端」(§13)，因為它要參照
//   §7 (SHIELD_WALL_*_BY_POWER / HEAL_RATIO) + §8 (LIGHTNING_PUDDLE_BONUS) 的常數，
//   TypeScript 不允許 const 提前引用。實際的物件 / 數值都在 §13。

// 為了讓 ELEM index 與 SPELLS key 對應的人能在這裡 grep 到，
// 列出法術清單（實際內容見 §13）：
//   ELEM[1] Fire          → SPELLS.Fire          (damage + ignite)
//   ELEM[2] Water         → SPELLS.Water         (heal)
//   ELEM[3] Wind          → SPELLS.Wind          (shieldPhys + shieldMag)
//   ELEM[4] Light         → SPELLS.Light         (buffAtk)
//   ELEM[5] Lightning     → SPELLS.Lightning     (damage + puddleBonus)
//   ELEM[6] WindFriendly  → SPELLS.WindFriendly  (whirlwindPull + heal)
//   ELEM[7] WindHostile   → SPELLS.WindHostile   (whirlwindPull + buffAtk 負值)
//   ELEM[8] Grow          → SPELLS.Grow          (growGrass: 空地→草 / 既有草→root 敵)
//   ELEM[9] Rain          → SPELLS.Rain          (rainPuddle: 空地→水 / 既有水→%maxHP 傷害)
//   ELEM[10] Rockfall     → SPELLS.Rockfall     (rockfall: 單格；空地→boulder / 有人→%maxHP 巨量 dmg)

// ============================================================
// §3 基地座標 + HP
// ============================================================
//
// AI 基地在 row 0，玩家基地在 row 7。每個基地占 2 格（(3,y)+(4,y)）。
// HP 是基地共用血量；任一基地歸 0 即遊戲結束。
//
// 平衡性方向：
//   - HP ↑ → 場次更長、有翻盤空間（適合測試法術節奏）
//   - HP ↓ → 強制快攻、衝鋒陣容變強
//   - 雙方 HP 不對稱 → 可以做「玩家挑戰模式」（譬如 AI HP 100、玩家 180）
//
// 不建議改座標 — Renderer 與佈陣區判定都假設基地在 row 0 / 7 的中央 4 格。

export const BASES: BaseConfig[] = [
  { team: 0, tiles: [{ x: 3, y: 7 }, { x: 4, y: 7 }], hp: 180 },  // Player base (bottom)
  { team: 1, tiles: [{ x: 3, y: 0 }, { x: 4, y: 0 }], hp: 180 },  // AI base (top)
];

// ============================================================
// §4 Match-3 計分（Points & Tiers，retroactive chain rewards）
// ============================================================
//
// 公式：
//   points = 10 × min(MATCH3_SIZE_COMBO_CAP,
//                     sizeMultiplier(groupSize) × chainStepMult(stepIdx, effChainDepth))
//
// 全部設計理由 / 情境試算見規格書 §2.4a；UI 反饋見 CLAUDE.md「Match-3 積分系統」。
//
// 平衡性方向參考：
//   - 提高 SIZE_MULT[4/5] → 鼓勵組大消除（4/5 連的回報變高）
//   - 提高 CHAIN_MULT 中段（idx 2~3）→ 鼓勵「製造連鎖」而非單發大消除
//   - 拉低 SPELL_LEVEL_THRESHOLDS → 法術升等變快、遊戲節奏變激烈
//   - 拉高 → 法術升等變慢、佈陣物理派更強

/** 每個群組大小的倍率（3-連 = 1.0×，4 = 2.0×，5 = 3.5×）。6+ 走 _CAP。 */
export const MATCH3_SIZE_MULT: Record<number, number> = {
  3: 1.0,
  4: 2.0,
  5: 3.5,
};

/** 6+ 連的封頂倍率（取代 MATCH3_SIZE_MULT 表查不到的 key）。 */
export const MATCH3_SIZE_MULT_CAP = 5.0;

/**
 * size × combo 乘積的上限；最終再 ×10 換成 points。
 * 撞 cap → 該群組就停在 10 × cap = 100 分。降低 cap 會壓抑大連鎖暴衝。
 */
export const MATCH3_SIZE_COMBO_CAP = 10;

/**
 * Chain 倍率查表（retroactive，由「最終深度 N」回推）。
 *
 * 在最終深度 N 下，第 k 步（1-indexed）取 MATCH3_CHAIN_MULT[N - k + 1]：
 *   N=1: [1.0]
 *   N=2: [2.5, 1.0]
 *   N=3: [4.0, 2.5, 1.0]
 *   N=4: [6.0, 4.0, 2.5, 1.0]
 *   N=5+: [6.0, 4.0, 2.5, 1.0, 1.0, …]   （MATCH3_CHAIN_MULT_CAP_DEPTH 後固定 ×1.0）
 *
 * Index 0 是 placeholder（不用，永遠不會被讀）。
 * 改數字 → 直接影響「主動 swap 製造連鎖」的價值。
 *
 * ⚠️ **陣列長度必須 = MATCH3_CHAIN_MULT_CAP_DEPTH + 1**（包含 index 0）。
 *     拉長陣列必須同步拉高 _CAP_DEPTH，否則尾巴值不會被讀到。
 */
export const MATCH3_CHAIN_MULT: ReadonlyArray<number> = [0, 1.0, 2.5, 4.0, 6.0];

/** Chain depth 用於查表的上限（超過此值一律當這個值處理；目前 = 4）。 */
export const MATCH3_CHAIN_MULT_CAP_DEPTH = 4;

/**
 * 累計 points 到法術 powerLevel 的階梯。
 * 例：累計 35 分 → Lv 3；累計 180 分 → Lv 7 (MAX)。
 *
 *   閾值 < 10  → Lv 0（該元素本回合無法施法）
 *   10        → Lv 1
 *   20        → Lv 2
 *   35        → Lv 3 ← Shield Wall 才開始有效（power<3 一律 0/0）
 *   60        → Lv 4
 *   90        → Lv 5
 *   130       → Lv 6
 *   180+      → Lv 7（MAX）
 *
 * 想讓法術早點觸發：拉低前幾項閾值（例如 [5, 15, 25, …]）。
 *
 * ⚠️ 陣列長度直接決定 SPELL_LEVEL_MAX（下面那個常數自動同步）。
 *    拉長 / 縮短這個陣列 → SPELL_LEVEL_MAX 跟著變 → §7 的兩個 SHIELD_WALL_*_BY_POWER
 *    陣列也要拉到 length >= SPELL_LEVEL_MAX + 1，不然 cap 邏輯讀到 undefined。
 */
export const SPELL_LEVEL_THRESHOLDS: ReadonlyArray<number> = [10, 20, 35, 60, 90, 130, 180];

/** Spell powerLevel 上限（自動 = SPELL_LEVEL_THRESHOLDS.length）。不要手動改這個。 */
export const SPELL_LEVEL_MAX = SPELL_LEVEL_THRESHOLDS.length;  // 目前 = 7

// ============================================================
// §5 Phase 計時 / 上限
// ============================================================
//
// 控制每回合三消階段、排程階段的長度。
//
// 模式差異：
//   - PvP / Story (網路 / 教學模式): 用這些秒數做倒數
//   - Single Player (本機 vs AI):  忽略秒數，HUD 顯示 ∞
//   - 兩種模式都受 `MAX_SWAPS_PER_ROUND` 約束（手動 swap 次數）
//
// `MAX_SWAPS_PER_ROUND` 同時是 server replay 防作弊的硬上限 —
// client 跳過 gate 也會被 `match3Replay.reconcileMatchResults` 駁回。
//
// 平衡性方向：
//   - 拉低 swap 上限 → 強制玩家精算每一手，遊戲節奏變慢
//   - 拉高 MATCH3_TIMER_SECS → 給玩家更多思考時間，但拖長 PvP 場次
//   - SCHED_TIMER_SECS 主要影響法術新手友善度，30s 已是寬鬆值

export const MATCH3_TIMER_SECS = 30;
export const SCHED_TIMER_SECS = 30;
export const MAX_SWAPS_PER_ROUND = 10;

// ============================================================
// §6 Match-3 寶石元素範圍
// ============================================================
//
// 寶石可以滾出的元素 index 範圍（對應 constants.ts 的 ELEM 表）。
//
// 目前 ELEM 順序：[0:None, 1:Fire, 2:Water, 3:Wind, 4:Light, 5:Lightning]
//   minGemElement=1 跳過 None（None 是 placeholder，不該出現在棋盤）
//   maxGemElement=5 涵蓋所有 5 元素
//
// 想暫時把某元素從棋盤拔掉（測平衡性，譬如想試「沒有治療的場次」）：
//   - 只能拔最尾端：把 maxGemElement 設為 4 → Lightning 不再出現
//   - 想拔中間（如拔 Water=2）：需要重排 ELEM 順序（動 constants.ts），
//     不然 maxGemElement 卡不住。

export const M3: M3Config = {
  minGemElement: 1,
  maxGemElement: 5,
};

// ============================================================
// §7 法術數值（治療 / Shield Wall / 額外效果）
// ============================================================

/**
 * 治療比例：`healed = ceil(maxSoldiers × HEAL_RATIO × powerLevel)`。
 *
 * 例：HEAL_RATIO=0.06、powerLevel=5、Infantry maxSoldiers=60
 *   → ceil(60 × 0.06 × 5) = 18 兵（30% 滿血）
 *
 * 平衡性方向：
 *   - 提高 → 治療派變強、推進更難擊穿
 *   - 降低 → 法術升等的「治療路線」變得 niche
 *
 * 不要設 0：spell.ts 不會走早退；該元素法術會變成「點到坐標但全程 0 治療」。
 */
export const HEAL_RATIO = 0.06;

/**
 * Wind / Shield Wall 物理減免查表 — index = powerLevel (0..SPELL_LEVEL_MAX)。
 *
 * 規則：
 *   - powerLevel < 3 → 0%（連鎖深度不足、Shield Wall 不啟動）
 *   - powerLevel ≥ MAX → 自動 clamp 到陣列最後一格（cap）
 *   - 物理減免 cap 通常設較低（不能完全免疫物理打擊）
 *
 * 設計目的：高等 Shield Wall 對物理 / 法術擋的程度不同（魔法擋更多 → §下）。
 *
 * ⚠️ **陣列長度必須 ≥ SPELL_LEVEL_MAX + 1**（包含 index 0）。
 *    若 §4 改了 SPELL_LEVEL_THRESHOLDS 長度，這裡也要對應拉長。
 *    現在 SPELL_LEVEL_MAX = 7，陣列長 8（index 0..7）剛好。
 *
 * 值的語意：0.30 = -30% 物理傷害；1.0 = 完全免疫（不建議）。
 */
export const SHIELD_WALL_PHYS_BY_POWER: ReadonlyArray<number> = [
  0,     // power 0  ← 無效，placeholder
  0,     // power 1  ← Shield Wall power<3 不觸發
  0,     // power 2  ← 同上
  0.30,  // power 3  ← 第一階有效
  0.40,  // power 4
  0.50,  // power 5
  0.60,  // power 6  ← 物理 cap 60%
  0.60,  // power 7  ← MAX，cap 維持 60%
];

/**
 * Wind / Shield Wall 法術減免查表 — 同上索引規則。
 *
 * 設計上法術 cap 較高（90%）因為法術直接成傷而非經由 atk × hp 計算，
 * 高等 Shield Wall 才能跟高 power 法術抗衡。
 *
 * ⚠️ 長度必須與 SHIELD_WALL_PHYS_BY_POWER 一致。
 */
export const SHIELD_WALL_MAG_BY_POWER: ReadonlyArray<number> = [
  0,     // power 0
  0,     // power 1
  0,     // power 2
  0.60,  // power 3  ← 物理 30% 對應法術 60% — 法術擋更多
  0.70,  // power 4
  0.80,  // power 5
  0.90,  // power 6  ← 法術 cap 90%（接近免疫但保留 1 點突破空間）
  0.90,  // power 7  ← MAX
];

// ============================================================
// §8 戰鬥地形互動
// ============================================================
//
// 兩個倍率分別控制：burningGrass 的 DOT / Lightning 射積水。
// 改一個只影響該機制，互不耦合。
//
// Session 12 follow-up — `ARCHER_GRASS_COVER_MULT`（弓兵打草地折扣）
// 已移除；草地不再降低弓兵傷害。`puddle` 也不再減速騎兵（movement.ts
// 已拿掉對應的 budget 扣減），只剩下 Lightning vs puddle 的傷害加成。

/**
 * 燒草 (burningGrass) 每 tick 對站在上面的單位造成的 DOT 比例。
 *   dmg = ceil(maxSoldiers × TERRAIN_BURN_DOT_RATIO)
 *
 * 例：0.15 + Infantry maxSoldiers=60 → 每 tick 9 傷害（算 magic damage，
 *     會被 tickDefMag 折抵）。
 *
 * 拉高 → 燒草更具威脅（誘導玩家清燒草 / 避走）
 * 拉低 → 燒草只是視覺效果（兩 tick 才扣 5 兵）
 */
export const TERRAIN_BURN_DOT_RATIO = 0.15;

/**
 * Lightning 打站在積水上的目標時的傷害加成。
 *   - 1.30 = +30% 傷害
 *   - 1.0  = 無加成（拔掉 Lightning vs puddle 互動）
 *   - 2.0  = 雙倍傷害（極端，會讓積水佈陣變廢）
 *
 * 加成套用在 tickDefMag 折抵之前。
 */
export const LIGHTNING_PUDDLE_BONUS = 1.30;

// ─────────────────────────────────────────────────────────────
// Leader CCC (Session 11) — Grow / Rain dual-mechanic spells
// ─────────────────────────────────────────────────────────────

/**
 * Rain (降雨) 打在「**原本就是積水**」的格子上時，對該格的敵方單位
 * 造成最大兵力的多少 % 傷害。每個 powerLevel 一格的查表。
 *
 *   index = powerLevel；index 0 = placeholder（power 0 無法施法）
 *
 * 算 magic damage → 套 tickDefMag 折抵（同 Step F DOT / Step D Lightning）。
 * Rain 在空地畫積水的部分**不**造成傷害（只純地形改變）。
 *
 * 預設 8% → 32% 跨 Lv1..Lv7，對厚血單位也能造成可感壓力但不會秒殺。
 *
 * ⚠️ 陣列長度必須 ≥ SPELL_LEVEL_MAX + 1。
 */
export const RAIN_PUDDLE_DROWN_BY_POWER: ReadonlyArray<number> = [
  0,     // power 0  ← placeholder（無法施法）
  0.08,  // power 1  ← 8% maxSoldiers
  0.12,  // power 2
  0.16,  // power 3
  0.20,  // power 4
  0.24,  // power 5
  0.28,  // power 6
  0.32,  // power 7  ← MAX 32% maxSoldiers
];

/**
 * Grow (生長) 在「**原本就是草地**」的格子上時，對該格敵方單位施加的
 * rooted modifier 持續 ticks。
 *
 *   - Grow 是 hostile phase (Step D cast)，所以 durationTicks=2 → 涵蓋
 *     1 個 Step B 移動（cast tick 已過 Step C；end-of-tick 後 ticksRemaining=1；
 *     下一 tick Step B 看見 rooted=true；end-of-tick 後 ticksRemaining=0 過期）。
 *   - 若要讓敵方錯過 2 次 Step B → 設為 3。
 *   - 若要做 powerLevel scaling（高等持續更久），改 SpellDef 直接寫
 *     `rootDurationTicks: 高等就拉長`（目前沒這需求）。
 */
export const GROW_ROOT_DURATION_TICKS = 2;

/**
 * Grow (生長) 在「**非草地**」格子上時（= 沒有 'grass' 的 'none' /
 * 'puddle' / 'burningGrass'），對該格敵方單位施加的 'defDown' 修飾子
 * 強度查表。Index = powerLevel；index 0 = placeholder（無法施法）。
 *
 * 數值意義：value 0.05 = 該單位接下來受傷 ×1.05（5% more damage taken）。
 * 多筆 defDown 用 SUM 聚合到 `BattleUnit.dmgTakenMult = 1 + Σ`。
 *
 * Lv1=2%, Lv7=22% — 預設 curve 設計目標：低等級給「聊勝於無」的感覺，
 * 高等級給可感的戰術價值（讓對手後排脆皮被秒的機率明顯上升）但不到秒殺。
 *
 * ⚠️ 陣列長度必須 ≥ SPELL_LEVEL_MAX + 1。
 */
export const GROW_DEFDOWN_BY_POWER: ReadonlyArray<number> = [
  0,     // power 0  ← placeholder
  0.02,  // power 1  ← +2% damage taken
  0.04,  // power 2
  0.07,  // power 3
  0.10,  // power 4
  0.14,  // power 5
  0.18,  // power 6
  0.22,  // power 7  ← MAX +22%
];

/**
 * Grow 的 defDown modifier 持續 ticks。Hostile-phase cast 同 `GROW_ROOT_DURATION_TICKS`
 * 規則：2 → 涵蓋下一 tick 的 Step C combat + Step D hostile spell + Step F DOT
 * 的所有傷害事件。
 */
export const GROW_DEFDOWN_DURATION_TICKS = 2;

/**
 * Rain (降雨) 在「**非積水**」格子上時，對該格敵方單位造成的「毛毛雨」
 * 傷害比例查表。Index = powerLevel。
 *
 * 數值意義：value 0.02 = 扣 ceil(maxSoldiers × 0.02) = 2% 滿員兵力。
 * 走 magic damage path（套 tickDefMag 折抵 + 經 applyDamageToUnit 套
 * dmgTakenMult），與 puddle bonus 那條同流。
 *
 * 預設 curve：刻意設計成「上限也很小」(Lv7 = 7%)。對 Infantry maxSoldiers=60
 * → Lv7 ≈ 5 兵；對 Cavalry maxSoldiers=40 → Lv7 ≈ 3 兵。對比既有的
 * RAIN_PUDDLE_DROWN_BY_POWER (Lv7 = 32%) 仍然小 4-5 倍，符合「直接施只是
 * 聊勝於無、玩家應該配合 Rain 先畫再 Rain 觸發 puddle bonus」的設計意圖。
 *
 * ⚠️ 陣列長度必須 ≥ SPELL_LEVEL_MAX + 1。
 */
export const RAIN_DIRECT_DMG_BY_POWER: ReadonlyArray<number> = [
  0,      // power 0  ← placeholder
  0.01,   // power 1  ← 1% maxSoldiers
  0.02,   // power 2
  0.03,   // power 3
  0.04,   // power 4
  0.05,   // power 5
  0.06,   // power 6
  0.07,   // power 7  ← MAX 7% maxSoldiers（仍遠低於 puddle 的 32%）
];

// ─────────────────────────────────────────────────────────────
// Leader CCC (Session 12) — Rockfall single-cell hostile spell
// ─────────────────────────────────────────────────────────────

/**
 * Rockfall 命中已有單位時，對該格敵方造的 % maxSoldiers 巨量傷害查表。
 * Index = powerLevel；index 0 = placeholder。
 *
 * 走 magic damage path：套 tickDefMag 折抵 + applyDamageToUnit 套
 * dmgTakenMult。Lv1=15% → Lv7=60% — 大幅高於 Rain 既有 puddle
 * (Lv7=32%)，因為 Rockfall 半徑只有 1 格，命中代價就是「賭對位置」。
 *
 * 平衡性方向：
 *   - 拉高 → Rockfall 變成 nuke；Lv7 接近 instant-kill 後排脆皮
 *   - 拉低 → Rockfall 變成「放石頭擋路」為主，傷害為輔的工具型法術
 *
 * ⚠️ 陣列長度必須 ≥ SPELL_LEVEL_MAX + 1。
 */
export const ROCKFALL_DMG_BY_POWER: ReadonlyArray<number> = [
  0,     // power 0  ← placeholder（無法施法）
  0.15,  // power 1  ← 15% maxSoldiers
  0.22,  // power 2
  0.30,  // power 3
  0.38,  // power 4
  0.46,  // power 5
  0.54,  // power 6
  0.60,  // power 7  ← MAX 60% maxSoldiers
];

// ============================================================
// §9 地形生成
// ============================================================
//
// 場地中央 4 列（rows 2..5）會隨機長出 grass + puddle cluster。
// 每塊大小 3..5 連通格。改數字會讓地圖更滿 / 更稀疏。
//
// 想要「沒有地形」的純競技測試：把兩個 _CLUSTERS 都設為 0。
// 想要「滿地形」的混亂測試：CLUSTERS=4, SIZE_MAX=8。

/**
 * 可生成地形的 Y 範圍（inclusive）。
 *
 * ⚠️ **強烈不建議侵入雙方部屬區**（rows 0-1 = AI 區、rows 6-7 = 玩家區），
 *    會讓玩家初始就站在不利地形上、體驗很差。預設 2..5 是「中央緩衝帶」。
 *
 * 如果要做「玩家從草地起跑」的特殊模式，把 MIN_Y 設 0、MAX_Y 設 7 即可，
 * 但記得 terrain.test.ts 的 row range assertion 也要對應放寬。
 */
export const TERRAIN_MIN_Y = 2;
export const TERRAIN_MAX_Y = 5;

/**
 * 每塊 cluster 的目標格數範圍。隨機 walk 會嘗試長到 MAX；長不到就接受 MIN。
 * MIN > MAX 會 throw（terrain.ts 有 assertion）。
 */
export const TERRAIN_CLUSTER_SIZE_MIN = 3;
export const TERRAIN_CLUSTER_SIZE_MAX = 5;

/**
 * 每場生成幾塊 cluster（grass / puddle 各自）。
 *
 * 上限參考：rows 2..5 × 8 cols = 32 格；4 塊 × 5 大小 = 20 格 → 仍有空隙。
 * 超過會出現 cluster 重疊失敗 / 生不出來 — terrain.ts 的演算法會略過。
 */
export const TERRAIN_GRASS_CLUSTERS = 2;
export const TERRAIN_PUDDLE_CLUSTERS = 2;

// ============================================================
// §10 佈陣規則 (DeploymentRules)
// ============================================================
//
// 「佈陣模式」單一信任來源。一個 game mode 啟動時，Deployment 套上
// 一份 DeploymentRules，整個系統（UI、cap、存檔驗證、AI 對手生成）
// 都讀這份規則，沒有第二份來源。
//
// 兩種 kind：
//   - 'manual' : 玩家自己擺位置 + 畫路徑。Single / PvP 用。
//                必有 composition cap — 雙方共用同一份，server 強制驗證。
//                未來「3v3 快戰」「10v10 史詩」等對戰模式都在這裡加 entry。
//   - 'preset' : 陣型 + 路徑骨架由腳本決定（Story 章節 / Casual 關卡）。
//                玩家只能畫路徑。沒有 cap — 腳本是 source of truth。
//                所有腳本模式共用同一份 PRESET_RULES。
//
// Single player = 對手換 AI 的 PvP 簡化版 — 套同一份 manual rule，
// AI 用同一份 composition 對等出兵。
//
// ⚠️ 改完 cp 到 server 同步（balance.ts 鏡像守則）。

// --- 規則型別 -------------------------------------------------------

/** 佈陣規則 — Deployment.rules 持有，Renderer / Input / ArmyData 都讀。 */
export type DeploymentRules =
  | {
      kind: 'manual';
      /** 程式碼用識別字（如 'standard'）。 */
      id: string;
      /** 顯示名稱（未來 PvP 模式選單會用）。 */
      name: string;
      /** [兵種, max] — 雙方共用的兵力上限。 */
      composition: ReadonlyArray<readonly [string, number]>;
    }
  | {
      kind: 'preset';
      id: string;
    };

// --- Manual 模式表 --------------------------------------------------
//
// Single / PvP 啟動時挑一個套上去。未來「3v3 快戰」「10v10 史詩」
// 等模式在這裡加 entry，server / client 都讀同一份避免漂移。
//
// 加 / 刪兵種：改 `composition` 即可（同時要確保該 type 在 UCFG /
// mkUnit 有對應實作）。想加 Mage：補 `['Mage', 1]`。

export const MANUAL_RULES = {
  standard: {
    kind: 'manual',
    id: 'standard',
    name: '標準對戰 (5 隻)',
    composition: [
      ['Infantry', 2],
      ['Cavalry', 1],
      ['Archer', 2],
    ],
  },
  // 未來新模式範例（暫不啟用，留供參考）：
  // small3v3: {
  //   kind: 'manual', id: 'small-3v3', name: '3v3 快戰',
  //   composition: [['Infantry', 1], ['Cavalry', 1], ['Archer', 1]],
  // },
  // epic10v10: {
  //   kind: 'manual', id: 'epic-10v10', name: '10v10 史詩',
  //   composition: [['Infantry', 4], ['Cavalry', 2], ['Archer', 4]],
  // },
} as const satisfies Record<string, DeploymentRules>;

/** Preset 模式 — Story / Casual 共用這份（沒有 cap、UI 隱藏 palette）。 */
export const PRESET_RULES: DeploymentRules = {
  kind: 'preset',
  id: 'preset',
} as const;

/**
 * 預設 manual 規則 — single / 未指定 mode 的 PvP 都套這個。
 * 未來 PvP lobby 加模式選擇時，這個常數退役。
 */
export const DEFAULT_MANUAL_RULES: DeploymentRules = MANUAL_RULES.standard;

// --- AI 放置算法參數（與 composition 正交）---------------------------
// 這些是「AI 怎麼擺單位」的算法 tuning，不是「擺什麼單位」。
// Composition 由當前 manual rule 決定（AI 跟玩家對等使用同一份）。

/** AI 放單一兵種時的最大碰撞重試次數。 */
export const AI_MAX_PLACEMENT_ATTEMPTS = 50;
/** AI 為單位畫路徑的最小步數下限。實際 = ri(MIN, cfg.maxPath)。 */
export const AI_MIN_PATH_STEPS = 2;
/** AI 可放置的列範圍（含端點），對應 rows 0-1。 */
export const AI_ROW_RANGE: readonly [number, number] = [0, 1];

// ============================================================
// §11 純函式輔助（依賴上述數字）
// ============================================================
//
// 這裡的 helpers 邏輯極簡 — 主要是把 lookup table 包成 function 介面，
// 讓 callsite 能寫 `pointsToLevel(pts)` 而不必每次手動查 array。
//
// 💡 **單純調平衡性不會碰到這裡** — 改 §1~§10 的數字就好，本段會自動跟上。
//
// 什麼時候要動本段？
//   - 改 size 倍率「形狀」（不是改數字，是改公式 — 譬如想做非線性）
//   - 改 chain mult 的「查表方式」（從 retroactive 變 forward）
//   - 改 Shield Wall「插值方式」（從 lookup 變 formula）
//
// 動本段就要同步更新規格書 §2.4a / §3.3.2 / §5.6 / §5.2 對應段落。

/**
 * 累計 points → powerLevel (0..SPELL_LEVEL_MAX)。
 * points < 第一階閾值時回傳 0（該元素本回合不可施法）。
 */
export function pointsToLevel(points: number): number {
  if (!Number.isFinite(points) || points <= 0) return 0;
  let lv = 0;
  for (let i = 0; i < SPELL_LEVEL_THRESHOLDS.length; i++) {
    if (points >= SPELL_LEVEL_THRESHOLDS[i]) lv = i + 1;
    else break;
  }
  return lv;
}

/** 群組大小倍率：3 → 1.0×、4 → 2.0×、5 → 3.5×、6+ → MATCH3_SIZE_MULT_CAP。 */
export function sizeMultiplier(count: number): number {
  if (count >= 6) return MATCH3_SIZE_MULT_CAP;
  return MATCH3_SIZE_MULT[count] || 0;
}

/**
 * 第 k 個 cascade step（1-indexed）在最終 effChainDepth 下的 combo 倍率。
 * 用 MATCH3_CHAIN_MULT 表回推 — 早期 step 倍率大，尾巴 step 一律 ×1.0。
 */
export function chainStepMult(stepIdx: number, effChainDepth: number): number {
  if (stepIdx < 1) return 0;
  const cappedN = Math.min(Math.max(1, effChainDepth), MATCH3_CHAIN_MULT_CAP_DEPTH);
  const idx = Math.max(1, cappedN - stepIdx + 1);
  return MATCH3_CHAIN_MULT[idx];
}

/** 套用 size × combo cap 後的最終群組積分。 */
export function chainPointsForGroup(count: number, comboMult: number): number {
  const sm = sizeMultiplier(count);
  if (sm <= 0 || comboMult <= 0) return 0;
  return Math.round(10 * Math.min(MATCH3_SIZE_COMBO_CAP, sm * comboMult));
}

/** Back-compat：N-連在 no-combo 下的基礎分數。 */
export function basePointsForGroup(count: number): number {
  return chainPointsForGroup(count, 1);
}

/**
 * Shield Wall 物理 / 法術減免（讀 SHIELD_WALL_*_BY_POWER 查表）。
 * power < 3 一律 0/0；超過陣列尾端則 clamp 到最後一格（cap）。
 */
export function defenseReduction(power: number): { phys: number; mag: number } {
  if (power < 3) return { phys: 0, mag: 0 };
  const idx = Math.min(power, SHIELD_WALL_PHYS_BY_POWER.length - 1);
  return {
    phys: SHIELD_WALL_PHYS_BY_POWER[idx],
    mag: SHIELD_WALL_MAG_BY_POWER[idx],
  };
}

// ============================================================
// §12 領軍角色 (Leaders) — picks which 5 spells & 5 gem colors a player gets
// ============================================================
//
// 在 DEPLOY 階段選一位「領軍」，整場戰鬥就鎖住該領軍的五法術組合與
// 三消盤面顏色組合。Server 在 initBattle 時收下雙方 leader id，把
// `state.leaderByTeam` 填好，後續 validateSpellCommand / Match3Mirror
// 都會讀這份來決定該隊允許的 5 個 ELEM。
//
// 設計守則：
//   - `spellElements.length === gemElements.length === 5`
//     （HUD bar 數量、SCHED 選單格數都假設為 5；目前不打算放大）
//   - 兩個陣列必須是 ELEM index 的 valid subset（1..ELEM.length-1）
//   - `gemElements` 內的 ELEM id 不可重複
//   - 設計上 spellElements ⊆ gemElements，這樣「儲存能量的色 = 可釋放
//     法術的色」這個直覺成立；目前都是 1:1 設定
//
// 加新領軍：在 LEADERS 物件加一筆 entry；server / client 都讀同一份。
// 改完用 `cp` 同步到 puzzle-tactician-server/src/balance.ts。

export const LEADERS = {
  AAA: {
    id: 'AAA',
    name: 'AAA',
    blurb: '經典五元素：火 / 雷 / 補血 / 護盾 / 加攻',
    // 五元素照 ELEM 1..5：Fire / Water / Wind(Shield) / Light(Bless) / Lightning
    spellElements: [1, 2, 3, 4, 5],
    gemElements:   [1, 2, 3, 4, 5],
  } satisfies LeaderDef,
  BBB: {
    id: 'BBB',
    name: 'BBB',
    blurb: '風系特化：火 / 雷 / 補血 / 旋風友 / 旋風敵',
    // Fire / Water / Lightning + WindFriendly(6) + WindHostile(7)。
    // 故意挑非連續 ELEM id 來示範 spellElements / gemElements 可以 sparse。
    spellElements: [1, 2, 5, 6, 7],
    gemElements:   [1, 2, 5, 6, 7],
  } satisfies LeaderDef,
  CCC: {
    id: 'CCC',
    name: 'CCC',
    blurb: '地形操控：火 / 雷 / 落石 / 生長 / 降雨',
    // Fire(1) / Lightning(5) + Grow(8) + Rain(9) + Rockfall(10)。
    // Session 12 — Heal Wave (Water=2) 被 Rockfall(10) 取代。CCC 改走純
    // 地形操控路線，沒有補血路線；玩家要靠 Grow/Rain/Rockfall 控場 +
    // Fire/Lightning 推進。Other leaders (AAA / BBB) 仍保有 Water=Heal。
    // Grow / Rain / Rockfall 都是 hostile-phase 地形互動法術。
    spellElements: [1, 5, 8, 9, 10],
    gemElements:   [1, 5, 8, 9, 10],
  } satisfies LeaderDef,
} as const satisfies Record<string, LeaderDef>;

/** Default leader picked when none is specified (legacy / fresh DEPLOY). */
export const DEFAULT_LEADER_ID: LeaderId = 'AAA';

/**
 * Lookup helper. Returns the requested leader, or DEFAULT (AAA) when the
 * id is unknown / undefined. Never throws — saves callers an explicit
 * fallback at every read site.
 *
 * Implementation note: LEADERS' inferred type is the narrow shape
 * `{ readonly AAA: LeaderDef; readonly BBB: LeaderDef }`, so it can't
 * be indexed by an arbitrary `string` (= `LeaderId`). Cast once to
 * `Record<string, LeaderDef>` for the lookups; the `as const satisfies`
 * declaration above already guarantees value-shape safety at compile time.
 */
export function getLeader(id: LeaderId | undefined | null): LeaderDef {
  const table = LEADERS as Record<string, LeaderDef>;
  if (!id) return table[DEFAULT_LEADER_ID];
  return table[id] ?? table[DEFAULT_LEADER_ID];
}

// ============================================================
// §13 SPELLS (effects-list 模型，本檔尾端是為了 TDZ — 見 §2 註解)
// ============================================================
//
// 每筆 SPELLS[ELEM_NAME] 對應一個元素的法術定義。改動指引：
//   - 想加新法術 → 新增一筆 entry；別忘了在 constants.ts 的 ELEM 也加新元素
//                  + balance.ts §12 LEADERS 把它加進某領軍的 spellElements
//   - 想加副效果 → 在 effects[] 多 push 一個 EffectKind
//   - 想改特定等級的數值 → 把 EffectScaling 從 linear 換成 table
//
// 數值與舊版 (Session 9 之前) 1:1 對應 — 重構只動結構，沒動 balance。
// 旋風 (WindFriendly / WindHostile) 的副效果是 Session 10 新加：
//   - WindFriendly: 拉友軍 + 順帶小回血
//   - WindHostile : 拉敵軍 + 順帶小降攻

export const SPELLS: Record<string, SpellDef> = {
  Fire: {
    name: 'Fireball',
    radius: 1,
    phase: 'hostile',
    // Session 12 follow-up — friendly-fire: `'all'` scope makes Fire
    // damage every unit (friend or foe) in the spell radius. Designed
    // so misdirected casts on your own line have real consequences.
    scope: 'all',
    effects: [
      // dmg × power（與舊版 1:1 對應），同時點燃半徑內所有草地。
      // Session 12 — breakBoulder: true 讓火球擊破巨石擋路（Rockfall counter）。
      { kind: 'damage', amount: { kind: 'linear', perLevel: 4 }, ignite: true, breakBoulder: true },
    ],
  },
  Water: {
    name: 'Heal Wave',
    radius: 1,
    phase: 'friendly',
    scope: 'own',
    effects: [
      // healed = ceil(maxSoldiers × HEAL_RATIO × power)
      { kind: 'heal', ratio: { kind: 'linear', perLevel: HEAL_RATIO } },
    ],
  },
  Wind: {
    name: 'Shield Wall',
    radius: 2,
    phase: 'friendly',
    scope: 'own',
    effects: [
      // 兩個 effect 並列：phys + mag 各自查表，與舊版 SHIELD_WALL_*_BY_POWER 一致。
      // 重複施放（如 Wind + WindFriendly 同 tick）走 Math.max 取大值（spell.ts 處理）。
      { kind: 'shieldPhys', reduction: { kind: 'table', byPower: SHIELD_WALL_PHYS_BY_POWER } },
      { kind: 'shieldMag',  reduction: { kind: 'table', byPower: SHIELD_WALL_MAG_BY_POWER } },
    ],
  },
  Light: {
    name: 'Blessing',
    radius: 1,
    phase: 'friendly',
    scope: 'own',
    effects: [
      // buffAtk += 1.5 × power（tick-scoped）
      { kind: 'buffAtk', value: { kind: 'linear', perLevel: 1.5 } },
    ],
  },
  Lightning: {
    name: 'Lightning',
    radius: 1,
    phase: 'hostile',
    // Session 12 follow-up — friendly-fire (同 Fire 設計)。
    scope: 'all',
    effects: [
      // dmg × power；站在 puddle 上的目標享 LIGHTNING_PUDDLE_BONUS 加成。
      // Session 12 — breakBoulder: true 讓雷電也能擊破巨石（Rockfall counter）。
      { kind: 'damage', amount: { kind: 'linear', perLevel: 3 }, puddleBonus: LIGHTNING_PUDDLE_BONUS, breakBoulder: true },
    ],
  },
  // Whirlwind 兩支變體 (Session 10 加副效果)
  WindFriendly: {
    name: 'Whirlwind (Ally)',
    radius: 2,
    phase: 'friendly',
    scope: 'own',
    effects: [
      { kind: 'whirlwindPull' },
      {
        kind: 'heal',
        // ratio × maxSoldiers; Lv1=3% / Lv7=22%。Infantry maxSoldiers=60 → Lv7=14 兵。
        // 比 Heal Wave Lv7（60×0.06×7=ceil=26）小很多，定位是「順帶療癒」。
        ratio: { kind: 'table', byPower: [0, 0.03, 0.05, 0.08, 0.11, 0.14, 0.18, 0.22] },
      },
    ],
  },
  WindHostile: {
    name: 'Whirlwind (Foe)',
    radius: 2,
    phase: 'hostile',
    scope: 'enemy',
    effects: [
      { kind: 'whirlwindPull' },
      {
        kind: 'buffAtk',
        // 負值 = 降攻。Lv1=-0.3 / Lv7=-4。對 Cavalry (atk=1, hp=40, naive atk=40)
        // Lv7 → 36，影響溫和；對 Infantry (atk=0.5, hp=60, atk=30) → 26，相對更顯著。
        // 與 Bless +1.5×power（Lv7=+10.5）對稱地小，是「順帶削弱」定位。
        value: { kind: 'table', byPower: [0, -0.3, -0.6, -1.0, -1.5, -2.0, -3.0, -4.0] },
        // Session 10 — Whirlwind hostile casts in Step D (AFTER same-tick
        // combat) so durationTicks=1 would expire before the debuff is
        // ever read. durationTicks=2 keeps it alive through end-of-this-
        // tick decrement → next tick's Step C combat sees it → next
        // end-of-tick expires. Net: 1 tick of effective debuff.
        //
        // Set to 3+ if you want the debuff to last multiple combat ticks
        // (e.g. 4 = "all 3 ticks of the next round + this tick's leftover").
        durationTicks: 2,
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────
  // Leader CCC (Session 11) — Grow / Rain dual-mechanic spells
  // ─────────────────────────────────────────────────────────────
  Grow: {
    name: 'Grow',
    radius: 1,                  // 3x3 Manhattan（13 格中心+4 邊+4 對角=空間實際 5 格 4-鄰）
    phase: 'hostile',           // Step D cast — 與 Fire / Lightning 同 phase 路由
    scope: 'enemy',             // root + defDown 都只挑敵方
    effects: [
      // spell.ts 對範圍內每個合法敵方單位檢查所站格（mutually exclusive
      // per-target）：
      //   - 'grass'  → push rooted modifier（durationTicks=GROW_ROOT_DURATION_TICKS=2）
      //   - 其他     → push defDown modifier（value=GROW_DEFDOWN_BY_POWER[lv]，
      //                durationTicks=GROW_DEFDOWN_DURATION_TICKS=2）
      // 掃完所有單位後，對範圍內所有 'none' 格 → 變 'grass'（terrainGrow event）。
      //
      // 兩個分支都是 hostile phase + durationTicks=2 → 涵蓋下一 tick 的
      // Step B / Step C / Step D / Step F（root 影響 Step B；defDown 影響
      // 所有 incoming damage）。
      {
        kind: 'growGrass',
        rootDurationTicks: GROW_ROOT_DURATION_TICKS,
        defDownAmount: { kind: 'table', byPower: GROW_DEFDOWN_BY_POWER },
        defDownDurationTicks: GROW_DEFDOWN_DURATION_TICKS,
      },
    ],
  },
  Rain: {
    name: 'Rain',
    radius: 1,                  // 同 Grow
    phase: 'hostile',
    // Session 12 follow-up — Rain 是攻擊型（drown / drizzle 都是 magic dmg），
    // 跟 Fire / Lightning 一同走友軍誤傷規則。地形 paint 部分（rainTransition）
    // 不分敵我，與 scope 無關。
    scope: 'all',
    effects: [
      // 對範圍內每個合法敵方單位檢查所站格（mutually exclusive per-target）：
      //   - 'puddle' → 大 % maxSoldiers magic dmg（drownRatio table，Lv1=8% → Lv7=32%）
      //   - 其他     → 小 "毛毛雨" magic dmg（directDmgRatio table，Lv1=1% → Lv7=7%）
      // 兩條都走相同的 tickDefMag 折抵 + applyDamageToUnit (折 dmgTakenMult)。
      // 掃完所有單位後，對範圍內所有 'none' 格 → 變 'puddle'（terrainRain event）。
      {
        kind: 'rainPuddle',
        drownRatio: { kind: 'table', byPower: RAIN_PUDDLE_DROWN_BY_POWER },
        directDmgRatio: { kind: 'table', byPower: RAIN_DIRECT_DMG_BY_POWER },
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────
  // Leader CCC (Session 12) — Rockfall single-cell hostile spell
  // ─────────────────────────────────────────────────────────────
  Rockfall: {
    name: 'Rockfall',
    radius: 0,                  // 單一格目標（無擴散；玩家要精準預判落點）
    phase: 'hostile',           // Step D cast — 與 Fire / Lightning / Grow / Rain 同 phase
    // Session 12 follow-up — `'all'` scope：落石不分敵我，砸到誰都受巨量
    // 傷害。boulder paint 那一半本來就用 cast-time occupancy 判斷（不分
    // 敵我），這裡 scope 改 'all' 讓傷害分支也對齊。
    scope: 'all',
    effects: [
      // spell.ts 對中心格 (radius=0) 檢查：
      //   - 該格有任何活著的單位（friend or foe） → 巨石碎裂：
      //       對該格敵方單位造 ceil(maxSoldiers × dmgRatio) magic dmg；
      //       地形維持不變（碎裂沒留下任何石頭）。
      //   - 該格沒有任何單位 → 落下巨石：
      //       無論原地形（包含 'none' / 'grass' / 'puddle' / 'swamp' /
      //       'burningGrass'）一律覆蓋成 'boulder'。
      //       基地 tile 永遠跳過。
      //
      // 巨石(boulder)地形：
      //   - 阻擋移動：movement.ts 將 boulder 視為不可通行，遇到時自動
      //     嘗試另一軸繞道；若另一軸也不可通行則停在原地。
      //   - 不受 Grow / Rain 影響：transition 表都回 null。
      //   - 被 Fire / Lightning 擊破：damage effect 的 breakBoulder: true
      //     在 spell radius 內把 boulder 還原成 'none'。
      {
        kind: 'rockfall',
        dmgRatio: { kind: 'table', byPower: ROCKFALL_DMG_BY_POWER },
      },
    ],
  },
};
